import { bbox } from "@turf/turf";
import { apiFetch } from "./api";
import { GeoJSONFeature, MapLayerConfig, SearchResult } from "../types";
import * as wellknown from "wellknown";

// Interface for lightweight place data used for search
export interface SearchablePlaceItem {
  id: number | string;
  name: string;
  description?: string;
  thumbnail?: string;
  type?: string;
  label?: string;
  _title?: string;
  title?: string;
  [key: string]: any; // Allow for additional properties
}

export const checkValidGeojsonAndSupportedType = (geoJson: Record<string, any> | null) => {
  if (!geoJson || typeof geoJson !== "object" || !geoJson?.type) return null;

  const supportedTypes = ["Point", "LineString", "Polygon", "MultiPoint", "MultiLineString", "MultiPolygon", "GeometryCollection"];
  if (!supportedTypes.includes(geoJson?.type)) {
    return null;
  }

  // Validate coordinates exist and are not empty
  if (!geoJson.coordinates) {
    return null;
  }
  if (Array.isArray(geoJson.coordinates) && geoJson.coordinates.length === 0) {
    return null;
  }

  return geoJson;
};

/**
 * Parse WKT (Well-Known Text) format to GeoJSON using wellknown library
 */
export const wktToGeoJSON = (wktString: string) => {
  // NEW: Defensive check - only process strings
  if (typeof wktString !== "string") {
    console.warn("[WKT-PARSER] wktToGeoJSON called with non-string input:", typeof wktString);
    return null;
  }
  if (!wktString.trim()) {
    return null;
  }

  try {
    const geoJson = wellknown.parse(wktString);

    // Check if we got valid geojson and supported geometry types
    if (!checkValidGeojsonAndSupportedType(geoJson)) {
      console.warn("[WKT-PARSER] Unsupported geometry type:", geoJson?.type, "for WKT:", wktString);
      return null;
    }

    return geoJson;
  } catch (error) {
    // Handle "Unknown Geometry Type" and other WKT parsing errors gracefully
    if (error instanceof Error && error.message.includes("Unknown Geometry Type")) {
      console.warn("[WKT-PARSER] Unknown geometry type in WKT string:", wktString, "Error:", error.message);
    } else {
      console.error("[WKT-PARSER] WKT parsing error with wellknown library:", error, "WKT:", wktString);
    }

    // Return null instead of throwing - let the caller handle the fallback
    return null;
  }
};

/**
 * Fetch detail data for a specific place by ID
 */
export const fetchPlaceById = async (placeId: string | number, apiEndpoint: string, layerId: string, userProfile: any) => {
  try {
    // Handle Fulcrum data differently - it requires special processing
    if (apiEndpoint.includes("fulcrum")) {
      try {
        // Import the Fulcrum service dynamically
        const { fetchFulcrumRecordById } = await import("./fulcrumLibService");

        // Fetch the individual Fulcrum record
        const fulcrumRecord = await fetchFulcrumRecordById(placeId.toString(), userProfile);

        if (!fulcrumRecord) {
          return null;
        }

        // Extract geometry from the Fulcrum record
        const geometry = fulcrumRecord.geometry;
        if (!geometry) {
          return null;
        }

        // Create a feature from the Fulcrum record
        const feature = {
          properties: {
            ...fulcrumRecord,
            id: fulcrumRecord.id || placeId,
            name: fulcrumRecord.name || fulcrumRecord.title || fulcrumRecord._title || fulcrumRecord.label || `Fulcrum Item ${placeId}`
          },
          geometry: geometry,
          type: "Feature",
          layer: { id: layerId }
        };

        // Calculate center and zoom based on geometry type
        let center: [number, number];
        let zoom = 14;

        if (geometry.type === "Point") {
          center = geometry.coordinates;
        } else {
          // For other geometry types, calculate center from bbox
          const boundingBox = bbox(geometry);
          center = [(boundingBox[0] + boundingBox[2]) / 2, (boundingBox[1] + boundingBox[3]) / 2];
          zoom = 12;
        }

        const result = {
          feature,
          center,
          zoom
        };

        return result;
      } catch (error) {
        console.error("Error processing Fulcrum data", error);
        return null;
      }
    }

    // Standard processing for non-Fulcrum data
    // Ensure the URL is properly formatted
    const baseUrl = apiEndpoint.endsWith("/") ? apiEndpoint : `${apiEndpoint}/`;
    const itemUrl = `${baseUrl}${placeId}/`;

    // Fetch the specific place details from the API
    const placeData = await apiFetch(itemUrl);

    if (!placeData) {
      return null;
    }

    if (!placeData.geometry) {
      return null;
    }

    // Parse the GeoJSON geometry
    let geometry;
    try {
      if (typeof placeData.geometry === "string") {
        try {
          // Try to parse as JSON first
          geometry = JSON.parse(placeData.geometry);
        } catch (error) {
          // If that fails, try to parse as WKT
          geometry = wktToGeoJSON(placeData.geometry);
        }
      } else {
        // It's already an object
        geometry = placeData.geometry;
      }
    } catch (error) {
      return null;
    }

    if (!geometry) {
      return null;
    }

    // Create a feature object with the feature properties
    const feature = {
      properties: {
        ...placeData,
        // Ensure these key properties are always available
        id: placeData.id || placeId,
        name: placeData.name || placeData.title || placeData.label || `Item ${placeId}`
      },
      geometry: geometry,
      type: "Feature",
      layer: { id: layerId }
    };

    // Determine center coordinates based on geometry type
    let center: [number, number];
    let zoom = 14;

    if (geometry.type === "Point") {
      center = geometry.coordinates;
    } else if (geometry.type === "Polygon" || geometry.type === "MultiPolygon") {
      const boundingBox = bbox(geometry);
      // Use the center of the bbox
      center = [(boundingBox[0] + boundingBox[2]) / 2, (boundingBox[1] + boundingBox[3]) / 2];
      // Adjust zoom to show the entire polygon
      zoom = 12;
    } else if (geometry.type === "LineString") {
      // For lines, use the first point
      center = geometry.coordinates[0];
    } else {
      // Default fallback
      return null;
    }

    const result = {
      feature,
      center,
      zoom
    };

    return result;
  } catch (error) {
    return null;
  }
};

/**
 * Fetch lightweight search data for all searchable layers
 */
export const fetchSearchableLayersData = async (
  layers: MapLayerConfig[],
  userProfile: any,
  oAuthHeader?: Record<string, string>
): Promise<Record<string, SearchablePlaceItem[]>> => {
  // Only include explicitly searchable layers
  const searchableLayers = layers.filter((layer) => layer.searchable === true);

  if (searchableLayers.length === 0) return {};

  const searchData: Record<string, SearchablePlaceItem[]> = {};

  for (const layer of searchableLayers) {
    try {
      // Special handling for Fulcrum layers
      if (layer.type === "fulcrum") {
        try {
          // Early return if no user profile (e.g., during logout)
          if (!userProfile) {
            console.log(`[mapSearchUtils] No user profile available, skipping Fulcrum layer ${layer.id}`);
            continue;
          }

          // For Fulcrum layers, get the data from the Fulcrum API service
          const { fetchFulcrumRecordsAsGeoJSON } = await import("./fulcrumLibService");
          // @ts-ignore - TypeScript might not recognize formId on all layer types
          const formId = layer.formId;
          if (formId) {
            const geoJsonData = await fetchFulcrumRecordsAsGeoJSON(formId, userProfile);
            if (geoJsonData && geoJsonData.features && Array.isArray(geoJsonData.features)) {
              // Convert GeoJSON features to SearchablePlaceItem format
              searchData[layer.id] = geoJsonData.features.map((feature: GeoJSONFeature) => {
                const properties = feature.properties || {};
                // Extract main properties that we want to prioritize
                // Prioritize Fulcrum record ID fields over other ID fields
                const item = {
                  id:
                    properties._record_id || // Fulcrum GeoJSON standard field
                    properties.record_id || // Alternative record ID field
                    properties.id ||
                    properties._id ||
                    properties.fulcrum_id ||
                    `${layer.id}-${Math.random()}`,
                  name: properties.name || properties.label || properties._title || properties.title || `Item in ${layer.id}`,
                  description: properties.description || properties.notes || properties.comment || "",
                  type: layer.id,
                  label: properties.label,
                  _title: properties._title,
                  title: properties.title
                };

                // Add remaining properties that don't conflict with our main ones
                Object.entries(properties).forEach(([key, value]) => {
                  if (!item.hasOwnProperty(key)) {
                    (item as any)[key] = value;
                  }
                });

                return item;
              });
            }
          }
          continue; // Skip the rest of the loop for Fulcrum layers
        } catch (error) {
          console.warn(`Failed to fetch Fulcrum data for layer ${layer.id}:`, error);
          continue;
        }
      }

      // Use custom search API endpoint if specified, otherwise derive from layer URL
      let apiEndpoint;
      if (layer.searchApiEndpoint) {
        apiEndpoint = layer.searchApiEndpoint;
      } else {
        // Get the API endpoint from the layer URL
        // This assumes the layer.url is like "api/user/places-geojson/"
        // and we want to convert it to "api/user/places/"
        apiEndpoint = layer.url.includes("-geojson") ? layer.url.replace("-geojson", "") : layer.url.replace(/\/+$/, "");
      }

      // For apiFetch, we need to be careful with the URL
      if (layer.useOAuth) {
        try {
          // Don't include trailing slash when adding query params
          const baseUrl = apiEndpoint.endsWith("/") ? apiEndpoint.slice(0, -1) : apiEndpoint;

          // Get fields from layer config or use defaults
          const defaultFields = ["name", "id"];
          const searchFields = layer.searchableFields ? [...new Set([...defaultFields, ...layer.searchableFields])] : defaultFields;

          // Create the fields parameter string
          const fieldsParam = searchFields.join(",");

          // Build the URL
          const params = new URLSearchParams({ fields: fieldsParam });
          const fullUrl = baseUrl.includes("?") ? `${baseUrl}&${params.toString()}` : `${baseUrl}?${params.toString()}`;

          const response = await apiFetch(fullUrl, {
            credentials: "include",
            headers: oAuthHeader || {}
          });

          if (response && Array.isArray(response.results)) {
            searchData[layer.id] = response.results;
          } else if (response && Array.isArray(response)) {
            searchData[layer.id] = response;
          }
        } catch (error) {
          console.warn(`Failed to fetch searchable items for ${layer.id}:`, error);
          console.error("Error details:", error);
          continue;
        }
      } else {
        try {
          // For regular fetch, we can include the query params in the URL
          // Get fields from layer config or use defaults
          const defaultFields = "id,name";
          const fieldsParam = layer.searchableFields ? [...new Set(["id", "name", ...layer.searchableFields])].join(",") : defaultFields;

          const params = new URLSearchParams({
            fields: fieldsParam
          });
          const fullUrl = `${apiEndpoint}${apiEndpoint.includes("?") ? "&" : "?"}${params.toString()}`;

          const response = await fetch(fullUrl);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();

          if (data && Array.isArray(data.results)) {
            searchData[layer.id] = data.results;
          } else if (data && Array.isArray(data)) {
            searchData[layer.id] = data;
          }
        } catch (error) {
          console.warn(`Failed to fetch searchable items for ${layer.id}:`, error);
          console.error("Error details:", error);
          continue;
        }
      }
    } catch (error) {
      console.error(`Error processing searchable layer ${layer.id}:`, error);
    }
  }

  return searchData;
};

/**
 * Generate placeholder text for search based on available fields
 */
export const generateSearchPlaceholder = (layers: MapLayerConfig[]): string => {
  // Get all searchable layers with explicit searchable flag
  const searchableLayers = layers.filter((layer) => layer.searchable === true);

  // If no searchable layers, return a default placeholder
  if (searchableLayers.length === 0) {
    return "Search by coordinates (lat,lng)";
  }

  // Get unique searchable fields from all layers
  const allSearchableFields = new Set<string>();

  searchableLayers.forEach((layer) => {
    if (layer.searchableFields) {
      layer.searchableFields.forEach((field) => allSearchableFields.add(field));
    } else {
      // Default fields
      allSearchableFields.add("name");
    }
  });

  // Remove technical fields that users might not understand
  const displayFields = Array.from(allSearchableFields).filter((field) => !["id", "geometry", "_id", "fulcrum_id"].includes(field));

  // Create a comma-separated list with the last item joined by "or"
  let fieldsText = "";
  if (displayFields.length === 1) {
    fieldsText = displayFields[0];
  } else if (displayFields.length === 2) {
    fieldsText = `${displayFields[0]} or ${displayFields[1]}`;
  } else if (displayFields.length <= 4) {
    // Use all fields if there are 4 or fewer
    const lastField = displayFields.pop();
    fieldsText = `${displayFields.join(", ")} or ${lastField}`;
  } else {
    // Use just the first 3 fields if there are many
    fieldsText = `${displayFields[0]}, ${displayFields[1]} or other fields`;
  }

  // Always include coordinates option
  return `Search by ${fieldsText} or coordinates (lat,lng)`;
};

/**
 * Process search query against available data
 */
export const processSearchQuery = (
  query: string,
  searchableItemsData: Record<string, SearchablePlaceItem[]>,
  layers: MapLayerConfig[]
): SearchResult[] => {
  if (query.length < 2) {
    return [];
  }

  const results: SearchResult[] = [];

  // Check if it's a coordinate search (contains comma)
  if (query.includes(",")) {
    const coordinates = query.split(",").map((coord) => parseFloat(coord.trim()));

    if (coordinates.length === 2 && !coordinates.some(isNaN)) {
      const [lat, lng] = coordinates;

      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        results.push({
          id: "coords",
          name: `${lat}, ${lng}`,
          type: "coordinate",
          coordinates: [lng, lat]
        });
      }
    }
  }

  // Search in all searchable layers using the lightweight data
  const lowerQuery = query.toLowerCase();

  Object.entries(searchableItemsData).forEach(([layerId, items]) => {
    // Find the layer config
    const layerConfig = layers.find((layer) => layer.id === layerId);

    // Define which fields to search based on layer configuration
    const searchableFields = layerConfig?.searchableFields || ["name", "id"];

    items.forEach((item) => {
      // Check which fields match and store them
      const matchedFields: { field: string; value: string; match: string }[] = [];

      searchableFields.forEach((field) => {
        const fieldValue = item[field as keyof typeof item];
        if (fieldValue && typeof fieldValue === "string") {
          const stringValue = String(fieldValue);
          const lowerValue = stringValue.toLowerCase();

          if (lowerValue.includes(lowerQuery)) {
            // Find the exact match within the string to highlight later
            const matchIndex = lowerValue.indexOf(lowerQuery);
            const matchedPart = stringValue.substring(matchIndex, matchIndex + query.length);

            matchedFields.push({
              field,
              value: stringValue.length > 60 ? stringValue.substring(0, 60) + "..." : stringValue,
              match: matchedPart
            });
          }
        }
      });

      if (matchedFields.length > 0) {
        // Create a properties object with all searchable fields
        const properties: Record<string, any> = {
          type: item.type || layerId,
          // Include which fields matched for highlighting
          matchedFields
        };

        // Add essential properties
        Object.entries(item).forEach(([key, value]) => {
          if (["label", "_title", "title", "description"].includes(key) && value !== undefined) {
            properties[key] = value;
          }
        });

        results.push({
          id: item.id,
          name: item.name || item.label || item._title || item.title || `Item in ${layerId}`,
          type: layerId,
          layerId: layerId,
          properties
        });
      }
    });
  });

  return results;
};
